/**
 * Cart utility functions
 * Pure functions for cart calculations and operations
 */

/**
 * Calculate cart subtotal
 */
export const calculateCartSubtotal = cartItems => {
  return cartItems.reduce((total, item) => {
    const price =
      typeof item.price === 'string' ? parseFloat(item.price.replace('$', '')) : item.price;
    return total + price * item.quantity;
  }, 0);
};

/**
 * Calculate cart item count
 */
export const calculateCartItemCount = cartItems => {
  return cartItems.reduce((count, item) => count + item.quantity, 0);
};

/**
 * Find item in cart
 */
export const findCartItem = (cartItems, itemId, size = null, color = null) => {
  return cartItems.find(item => {
    if (size && color) {
      return item.id === itemId && item.size === size && item.color === color;
    }
    return item.id === itemId;
  });
};

/**
 * Check if cart is empty
 */
export const isCartEmpty = cartItems => {
  return !cartItems || cartItems.length === 0;
};

/**
 * Format cart for checkout
 */
export const formatCartForCheckout = cartItems => {
  return cartItems.map(item => ({
    id: item.id,
    name: item.name,
    price: typeof item.price === 'string' ? parseFloat(item.price.replace('$', '')) : item.price,
    quantity: item.quantity,
    size: item.size || null,
    color: item.color || null,
    image: item.image,
  }));
};

/**
 * Validate cart item
 */
export const validateCartItem = item => {
  if (!item) {
    return false;
  }
  if (!item.id || !item.name || !item.price) {
    return false;
  }
  if (item.quantity <= 0) {
    return false;
  }
  return true;
};

/**
 * Calculate shipping cost (placeholder logic)
 */
export const calculateShipping = (cartItems, shippingMethod = 'standard') => {
  const subtotal = calculateCartSubtotal(cartItems);

  if (subtotal >= 100) {
    return 0;
  } // Free shipping over $100

  switch (shippingMethod) {
    case 'express':
      return 15.99;
    case 'overnight':
      return 25.99;
    default:
      return 5.99;
  }
};

/**
 * Calculate tax (placeholder logic)
 */
export const calculateTax = (subtotal, taxRate = 0.08) => {
  return subtotal * taxRate;
};

/**
 * Calculate cart total
 */
export const calculateCartTotal = (cartItems, shippingMethod = 'standard', taxRate = 0.08) => {
  const subtotal = calculateCartSubtotal(cartItems);
  const shipping = calculateShipping(cartItems, shippingMethod);
  const tax = calculateTax(subtotal, taxRate);

  return {
    subtotal,
    shipping,
    tax,
    total: subtotal + shipping + tax,
  };
};
